import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts'

// SourceOptima brand-inspired color palette
const COLORS = [
  '#1e5045', // so-green
  '#f26522', // so-orange
  '#94c43d', // so-lime
  '#3b82f6', // blue
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f59e0b', // amber
]

// Normalize material names to consolidate variations
function normalizeMaterialName(name) {
  const lower = name.toLowerCase()

  // Consolidate all stainless steel variants
  if (lower.includes('stainless') || lower.includes('304') || lower.includes('316') || lower.includes('sst')) {
    return 'Stainless Steel'
  }
  // Consolidate aluminum variants
  if (lower.includes('aluminum') || lower.includes('aluminium')) {
    return 'Aluminum'
  }
  // Consolidate copper variants
  if (lower.includes('copper')) {
    return 'Copper'
  }
  // Consolidate polycarbonate/lexan variants
  if (lower.includes('polycarbonate') || lower.includes('lexan')) {
    return 'Polycarbonate'
  }
  // Consolidate delrin/acetal
  if (lower.includes('delrin') || lower.includes('acetal')) {
    return 'Delrin (Acetal)'
  }
  // Consolidate PVC variants
  if (lower.includes('pvc') || lower.includes('polyvinyl')) {
    return 'PVC'
  }
  // Consolidate steel (non-stainless)
  if (lower.includes('steel') && !lower.includes('stainless')) {
    return 'Steel'
  }

  return name
}

export default function MaterialPieChart({ data, totalWeight }) {
  // Consolidate materials by normalized name
  const consolidatedData = {}
  Object.entries(data).forEach(([name, value]) => {
    const normalized = normalizeMaterialName(name)
    consolidatedData[normalized] = (consolidatedData[normalized] || 0) + Number(value)
  })

  let chartData = Object.entries(consolidatedData).map(([name, value]) => ({
    name,
    value,
  }))

  // Sort by value descending
  chartData.sort((a, b) => b.value - a.value)

  // Limit to top 7 materials, consolidate rest as "Other"
  const MAX_ITEMS = 7
  if (chartData.length > MAX_ITEMS) {
    const topItems = chartData.slice(0, MAX_ITEMS)
    const otherValue = chartData.slice(MAX_ITEMS).reduce((sum, item) => sum + item.value, 0)
    if (otherValue > 0) {
      topItems.push({ name: 'Other Materials', value: otherValue })
    }
    chartData = topItems
  }

  const formatWeight = (value) => {
    return `${value.toFixed(2)} kg`
  }

  const formatPercent = (value) => {
    const percent = (value / totalWeight) * 100
    return `${percent.toFixed(1)}%`
  }

  const renderCustomLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
  }) => {
    if (percent < 0.05) return null // Don't show labels for small segments

    const RADIAN = Math.PI / 180
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5
    const x = cx + radius * Math.cos(-midAngle * RADIAN)
    const y = cy + radius * Math.sin(-midAngle * RADIAN)

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        className="text-xs font-medium"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    )
  }

  // Truncate long material names for legend
  const truncateName = (name, maxLen = 25) => {
    if (name.length <= maxLen) return name
    return name.slice(0, maxLen - 3) + '...'
  }

  return (
    <div className="w-full">
      {/* Chart */}
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomLabel}
              outerRadius={80}
              innerRadius={30}
              fill="#8884d8"
              dataKey="value"
              paddingAngle={2}
            >
              {chartData.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                  stroke="white"
                  strokeWidth={2}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value) => [formatWeight(value), 'Weight']}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      {/* Custom legend below */}
      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        {chartData.map((item, index) => (
          <div key={item.name} className="flex items-center gap-1.5 min-w-0">
            <div
              className="w-3 h-3 rounded-sm flex-shrink-0"
              style={{ backgroundColor: COLORS[index % COLORS.length] }}
            />
            <span className="text-gray-700 truncate" title={item.name}>
              {truncateName(item.name)} ({formatPercent(item.value)})
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
